import { Badge, Banner, BarList, Button, Card, Chip, Counter, DataTable, EmptyState, IconButton, Metric, Spinner, type Column } from '@ui';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  AlertIcon,
  BackForthIcon,
  CheckIcon,
  ClickIcon,
  ClockIcon,
  CloseIcon,
  DeadClickIcon,
  EyeIcon,
  FileCodeIcon,
  HourglassIcon,
  RefreshIcon,
  RepeatIcon,
  ScreensIcon,
  TrashIcon,
} from '../icons';
import { useConfirm } from '../jui';
import { UX_METRIC_LABELS, uxApi, type UxSession, type UxSuggestion, type UxSummary } from './uxApi';
import { mmss } from './uxWatch';

const noop = (): void => undefined;

export default function UxSessions({ onApiError = noop }: { onApiError?: (err: unknown) => void }) {
  const [sessions, setSessions] = useState<UxSession[] | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [removing, setRemoving] = useState<number | null>(null);
  const { confirm, dialog } = useConfirm();

  const load = useCallback(async () => {
    try {
      const res = await uxApi.list();
      setSessions(res.sessions);
      setError('');
    } catch (err) {
      onApiError(err);
      setError(err instanceof Error ? err.message : 'Не загрузилось');
    }
  }, [onApiError]);

  useEffect(() => {
    void load();
  }, [load]);

  const busy = sessions?.some((s) => s.status === 'queued' || s.status === 'running') ?? false;
  useEffect(() => {
    if (!busy) return;
    const timer = window.setInterval(() => void load(), 2000);
    return () => window.clearInterval(timer);
  }, [busy, load]);

  if (error) return <Banner tone="error">{error}</Banner>;
  if (!sessions) return <Spinner label="Загрузка" />;
  const remove = (session: UxSession) => {
    const when = new Date(session.startedAt ?? session.createdAt);
    void confirm({ title: `Удалить UX-сессию от ${when.toLocaleDateString('ru-RU')}?` }).then((yes) => {
      if (!yes) return;
      setRemoving(session.id);
      uxApi
        .remove(session.id)
        .then(() => {
          if (open === session.id) setOpen(null);
          return load();
        })
        .catch(onApiError)
        .finally(() => setRemoving(null));
    });
  };
  const columns = uxColumns(remove, removing !== null);
  const current = open === null ? undefined : sessions.find((session) => session.id === open);
  return (
    <div className="ux-list" data-testid="ux-sessions">
      {/* Ср2: the sessions are a DataTable like every table of the tracker — every column sorts. */}
      <DataTable
        className="bug-fixed-table ux-table"
        label="UX-сессии"
        columns={columns}
        rows={sessions}
        rowKey={(session) => session.id}
        pageSize={0}
        toolbar={
          <>
            <Metric icon={<EyeIcon />} value={sessions.length} label="Сессий" />
            <IconButton label="Обновить" icon={<RefreshIcon />} onClick={() => void load()} />
          </>
        }
        rowTone={(session) => (session.id === open ? 'selected' : undefined)}
        onRowClick={(session) => setOpen((prev) => (prev === session.id ? null : session.id))}
        empty={
          <EmptyState compact icon={<EyeIcon />}>
            Сессий нет · меню → «Последи за мной»
          </EmptyState>
        }
      />
      {current && <SessionDetail key={current.id} session={current} onChanged={() => void load()} onApiError={onApiError} />}
      {dialog}
    </div>
  );
}

function uxColumns(remove: (session: UxSession) => void, locked: boolean): Column<UxSession>[] {
  const whenOf = (session: UxSession) => new Date(session.startedAt ?? session.createdAt);
  const problems = (session: UxSession) => (session.summary ? alarmItems(session.summary).reduce((sum, item) => sum + item.value, 0) : null);
  const fresh = (session: UxSession) => session.suggestions.filter((item) => item.state === 'new').length;
  return [
    { key: 'status', header: 'Разбор', width: 84, value: (session) => session.status, render: (session) => <span data-testid={`ux-session-${session.id}`}><StatusBadge status={session.status} /></span> },
    {
      key: 'when',
      header: 'Когда',
      width: 130,
      value: whenOf,
      render: (session) => (
        <span className="ux-when nowrap" data-pii="">
          {whenOf(session).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}{' '}
          {whenOf(session).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
        </span>
      ),
    },
    { key: 'who', header: 'Кто', minWidth: 120, value: (session) => session.recorder ?? '', render: (session) => <span className="ux-who" data-pii="">{session.recorder ?? '—'}</span> },
    { key: 'duration', header: 'Длительность', width: 130, value: (session) => session.durationMs, render: (session) => <Metric icon={<ClockIcon />} value={mmss(session.durationMs)} label="Длительность" /> },
    { key: 'screens', header: 'Экранов', width: 100, value: (session) => session.summary?.screens ?? null, render: (session) => (session.summary ? <Metric icon={<ScreensIcon />} value={session.summary.screens} label="Экранов" short="экр." /> : <span className="ux-muted">—</span>) },
    { key: 'clicks', header: 'Нажатий на задачу', width: 150, value: (session) => session.summary?.clicksPerSegment ?? null, render: (session) => (session.summary ? <Metric icon={<ClickIcon />} value={session.summary.clicksPerSegment} label="Нажатий на задачу" short="нажатий" /> : <span className="ux-muted">—</span>) },
    { key: 'problems', header: 'Проблемы', minWidth: 220, value: problems, render: (session) => (session.summary ? <span className="jui-row ux-alarms"><Alarms items={alarmItems(session.summary)} /></span> : <span className="ux-muted">—</span>) },
    { key: 'fresh', header: 'Новых', width: 90, value: fresh, render: (session) => (fresh(session) > 0 ? <Counter className="ux-fresh" value={fresh(session)} strong tone="accent" label="Новых предложений" /> : <span className="ux-muted">—</span>) },
    {
      key: 'actions',
      header: '',
      headerLabel: 'Действия',
      width: 64,
      align: 'right',
      sortable: false,
      filterable: false,
      render: (session) => <IconButton label="Удалить сессию" icon={<TrashIcon />} disabled={locked} onClick={() => remove(session)} />,
    },
  ];
}

function StatusBadge({ status }: { status: UxSession['status'] }) {
  if (status === 'done') return <Badge tone="success" icon={<CheckIcon />} title="Разобрано" />;
  if (status === 'failed') return <Badge tone="error" icon={<AlertIcon />} title="Разбор упал" />;
  return <Spinner size="sm" label="Разбор идёт" />;
}

function SessionDetail({ session, onChanged, onApiError }: { session: UxSession; onChanged: () => void; onApiError: (err: unknown) => void }) {
  const [full, setFull] = useState<UxSession | null>(null);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    uxApi
      .get(session.id)
      .then((res) => setFull(res.session))
      .catch(onApiError);
  }, [session.id, session.status, session.suggestions.length, onApiError]);

  const act = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    try {
      await fn();
      onChanged();
      const res = await uxApi.get(session.id);
      setFull(res.session);
    } catch (err) {
      onApiError(err);
    } finally {
      setBusy('');
    }
  };

  const when = new Date(session.startedAt ?? session.createdAt);
  const view = full ?? session;
  return (
    <Card label={`UX-сессия №${session.id}`} flush className="ux-card ux-card-open" data-testid={`ux-detail-${session.id}`}>
        <div className="ux-body">
          <SummaryLine session={view} when={when} />
          {view.summary && (
            <div className="ux-metrics" data-testid="ux-metrics">
              <Metric icon={<ClockIcon />} value={mmss(view.summary.durationMs)} label="Длительность" short="длительность" />
              <Metric icon={<ScreensIcon />} value={view.summary.screens} label="Экранов" short="экранов" />
              <Metric icon={<ClickIcon />} value={view.summary.clicksPerSegment} label="Нажатий на задачу" short={UX_METRIC_LABELS.clicks_per_segment} />
              <Alarms items={alarmItems(view.summary)} />
            </div>
          )}
          {view.status === 'failed' && (
            <Banner
              tone="error"
              actions={
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<RefreshIcon />}
                  disabled={busy !== ''}
                  onClick={() => void act('retry', () => uxApi.retry(session.id))}
                >
                  Повторить
                </Button>
              }
            >
              <span data-pii="">{view.error}</span>
            </Banner>
          )}
          {(view.status === 'queued' || view.status === 'running') && (
            <div className="ux-muted ux-inline">
              <Spinner size="sm" label="Разбор идёт" /> разбор идёт
            </div>
          )}
          {full?.metrics && <ScreenBars rows={full.metrics.screenTime} />}
          {view.suggestions.length > 0 && (
            <div className="ux-sugs">
              {view.suggestions.map((item) => (
                <SuggestionCard
                  key={item.id}
                  item={item}
                  busy={busy === item.id}
                  onDecide={(decision) => void act(item.id, () => uxApi.decide(session.id, item.id, decision))}
                />
              ))}
            </div>
          )}
          {view.status === 'done' && view.error && <p className="ux-note" data-pii="">
              {view.error}
            </p>}
        </div>
    </Card>
  );
}

function alarmItems(s: UxSummary): Alarm[] {
  return [
    { icon: <DeadClickIcon />, value: s.deadClicks, label: 'Мёртвых нажатий', short: UX_METRIC_LABELS.dead_click },
    { icon: <BackForthIcon />, value: s.backAndForth, label: 'Туда-обратно', short: UX_METRIC_LABELS.back_and_forth },
    { icon: <RepeatIcon />, value: s.repeatedPaths, label: 'Повторов пути', short: UX_METRIC_LABELS.repeated_path },
    { icon: <HourglassIcon />, value: s.longPauses, label: 'Пауз дольше 8 с', short: UX_METRIC_LABELS.long_pause },
    { icon: <FileCodeIcon />, value: s.slowRequests, label: 'Запросов дольше 1,5 с', short: UX_METRIC_LABELS.slow_request },
  ];
}

/** Ux3: the one line on top of an opened session — who, when, how many problems (Ux1 words: none — «проблем нет»). */
function SummaryLine({ session, when }: { session: UxSession; when: Date }) {
  const problems = session.summary ? alarmItems(session.summary).reduce((sum, item) => sum + item.value, 0) : null;
  const day = when.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
  const time = when.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  return (
    <div className="ux-summary" data-testid="ux-summary">
      <span className="ux-who" data-pii="">
        {session.recorder ?? 'Неизвестно кто'}
      </span>
      <span className="ux-when" data-pii="">
        {day}, {time}
      </span>
      {problems === null ? (
        <span className="ux-muted">{session.status === 'failed' ? 'разбор не удался' : 'разбор идёт'}</span>
      ) : problems === 0 ? (
        <span className="ux-calm" data-bug-open="">
          <CheckIcon />
          проблем нет
        </span>
      ) : (
        <span className="ux-problems" data-bug-open="">
          <AlertIcon />
          {problems} {plural(problems, 'проблема', 'проблемы', 'проблем')}
        </span>
      )}
    </div>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  const tail = n % 100;
  if (tail >= 11 && tail <= 14) return many;
  if (n % 10 === 1) return one;
  return n % 10 >= 2 && n % 10 <= 4 ? few : many;
}

interface Alarm {
  icon: ReactNode;
  value: number;
  label: string;
  /** Seen next to the number, not only in the tooltip. */
  short: string;
}

/** Ux1 (report 217): counts that are a problem above zero — only those, red and with their words; none — «проблем нет». */
function Alarms({ items }: { items: Alarm[] }) {
  const raised = items.filter((item) => item.value > 0);
  if (raised.length === 0)
    return (
      <span className="ux-calm" data-bug-open="">
        <CheckIcon />
        проблем нет
      </span>
    );
  return (
    <>
      {raised.map((item) => (
        <Metric key={item.label} icon={item.icon} value={item.value} label={item.label} short={item.short} tone="error" />
      ))}
    </>
  );
}

function ScreenBars({ rows }: { rows: { screen: string; ms: number; visits: number }[] }) {
  return (
    <BarList
      label="Время на экранах"
      format={mmss}
      items={rows.slice(0, 6).map((row) => ({
        key: row.screen,
        value: row.ms,
        label: (
          <span title={row.screen} data-pii="">
            {row.visits > 1 && <b className="ux-visits">×{row.visits}</b>}
            {row.screen}
          </span>
        ),
      }))}
    />
  );
}

function SuggestionCard({ item, busy, onDecide }: { item: UxSuggestion; busy: boolean; onDecide: (decision: 'accept' | 'dismiss') => void }) {
  return (
    <article className={`ux-sug ux-sug-${item.state}`} data-testid={`ux-sug-${item.id}`}>
      <div className="ux-sug-main">
        <div className="ux-sug-title" data-pii="">
          {item.title}
        </div>
        {item.why && (
          <div className="ux-sug-why" data-pii="">
            {item.why}
          </div>
        )}
        <div className="ux-sug-tags">
          <Chip title="Объём работы">{EFFORT_WORDS[item.effort]}</Chip>
          {(item.block || item.file) && (
            <span data-pii="">
              <Chip color="amber-1" icon={<FileCodeIcon />} title={item.file ?? ''}>
                {item.block ?? item.file}
              </Chip>
            </span>
          )}
          {item.evidence.map((proof) => (
            <Chip key={proof.metric}>
              {UX_METRIC_LABELS[proof.metric]}
            </Chip>
          ))}
        </div>
      </div>
      <div className="ux-sug-actions">
        {item.state === 'new' && (
          <>
            <Button size="sm" variant="primary" icon={<CheckIcon />} disabled={busy} onClick={() => onDecide('accept')}>
              В задачи
            </Button>
            <Button size="sm" variant="secondary" icon={<CloseIcon />} disabled={busy} onClick={() => onDecide('dismiss')}>
              Отклонить
            </Button>
          </>
        )}
        {item.state === 'accepted' && (
          <Badge tone="success" icon={<CheckIcon />}>
            В задачах
          </Badge>
        )}
        {item.state === 'dismissed' && <span className="ux-muted">Отклонено</span>}
      </div>
    </article>
  );
}

const EFFORT_WORDS = { S: 'небольшая правка', M: 'средняя правка', L: 'большая правка' } as const;
